/**
 * Spec 086 — typed client for fan-out graph reads.
 *
 *   GET /api/v1/brands/:id/fanout/graph
 *   GET /api/v1/brands/:id/fanout/clusters
 *   GET /api/v1/brands/:id/fanout/clusters/:slug
 *   GET /api/v1/brands/:id/fanout/queries
 *
 * Consumed by:
 *   - Phase 9 Session B — CoverageDashboardPage cluster map
 *   - Phase 9 Session C — FanOutGraphPage + FanOutClusterDetailPage (this
 *     session adds the graph summary, cluster detail, and query member
 *     helpers below).
 */
import { apiGet } from '../lib/api.js';
import { apiPost } from '../lib/api-mutations.js';

export type FanoutSourceConfidence =
  | 'observed'
  | 'inferred_high'
  | 'inferred_medium'
  | 'inferred_low';

export interface FanoutClusterRow {
  slug: string;
  semantic_label: string;
  description: string | null;
  query_count: number;
  primary_sub_query_type: string;
  engine_coverage: Record<string, number>;
  last_recomputed_at: string | null;
  created_at: string;
}

export interface FanoutQueryMember {
  id: string;
  query_text: string;
  query_text_normalized: string;
  source_confidence: FanoutSourceConfidence;
  observation_count: number;
  engines_observed: string[];
  /**
   * Spec 217 — synthesized provenance only. Observed entries are excluded
   * server-side: one row held 31,822 of them and they were never read here
   * (extractSeedPromptIds keeps type==="synthesized"). Use
   * `observed_source_count` for how often the query was observed.
   */
  sources: Record<string, unknown>[];
  first_seen: string;
  last_seen: string;
  sub_query_type: string;
}

export interface FanoutClusterDetail extends FanoutClusterRow {
  /** The cluster's UUID — returned by the detail route (spec 106 cluster picker). */
  id: string;
  members: FanoutQueryMember[];
}

export interface FanoutGraphSummary {
  total_queries: number;
  total_clusters: number;
  observed_ratio: number;
  queries_by_confidence: {
    observed: number;
    inferred_high: number;
    inferred_medium: number;
    inferred_low: number;
  };
  queries_by_engine: Record<string, number>;
  last_recomputed_at: string | null;
}

export async function listFanoutClusters(
  brandId: string,
): Promise<FanoutClusterRow[]> {
  const res = await apiGet<{ data: FanoutClusterRow[] }>(
    `/api/v1/brands/${brandId}/fanout/clusters`,
  );
  return Array.isArray(res.data) ? res.data : [];
}

export async function getFanoutGraphSummary(
  brandId: string,
): Promise<FanoutGraphSummary> {
  const res = await apiGet<{ data: FanoutGraphSummary }>(
    `/api/v1/brands/${brandId}/fanout/graph`,
  );
  return res.data;
}

/**
 * Spec 136 — summary returned by the synthesize trigger (the synthesis tick's
 * `TickSummary`). Brand-scoped runs report per-brand stats under `per_brand[0]`;
 * the cluster count is `per_brand[0].clusters_total`. When the
 * `flag.worker.fanout_synthesis` flag is off the run is a no-op: `disabled` is
 * true and `per_brand` is omitted — callers must guard for that.
 */
export interface FanoutSynthesisPerBrand {
  brand_id: string;
  phase: string;
  queries_total: number;
  clusters_total: number;
  token_cost_usd: number;
  error?: string;
}
export interface FanoutSynthesisSummary {
  disabled: boolean;
  processed_brands: number;
  completed_brands: number;
  token_cost_usd: number;
  duration_ms: number;
  per_brand?: FanoutSynthesisPerBrand[];
}

/**
 * Spec 136 — POST /api/v1/brands/:id/fanout/synthesize.
 *
 * Runs capture + synthesis brand-scoped (takes a few seconds; ~cents). Returns
 * a synthesis summary that includes a cluster count under one of several
 * possible field names — see {@link readSynthesisClusterCount}.
 */
export async function synthesizeFanout(
  brandId: string,
): Promise<FanoutSynthesisSummary> {
  const res = await apiPost<{ data: FanoutSynthesisSummary }>(
    `/api/v1/brands/${brandId}/fanout/synthesize`,
    {},
  );
  return res.data ?? {};
}

/**
 * Defensive read of the cluster count from a synthesis summary
 * (`per_brand[0].clusters_total`). Returns `null` when the run was disabled
 * (flag off) or no per-brand row is present, signalling the caller to fall back
 * to refetching the graph summary for the count.
 */
export function readSynthesisClusterCount(
  summary: FanoutSynthesisSummary | null | undefined,
): number | null {
  const value = summary?.per_brand?.[0]?.clusters_total;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export async function getFanoutCluster(
  brandId: string,
  slug: string,
): Promise<FanoutClusterDetail> {
  const res = await apiGet<{ data: FanoutClusterDetail }>(
    `/api/v1/brands/${brandId}/fanout/clusters/${encodeURIComponent(slug)}`,
  );
  const data = res.data;
  // Defensive: backend should always return an array, but normalize in case.
  return {
    ...data,
    members: Array.isArray(data.members) ? data.members : [],
  };
}
